import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Globe, 
  Languages, 
  Target, 
  Users, 
  CheckCircle,
  ArrowRight,
  Play,
  TrendingUp,
  MapPin,
  MessageCircle
} from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { getAuthUrl } from "@/utils/redirectUtils";

const InternationalPage = () => {
  const benefits = [
    {
      icon: Languages,
      title: "Instant Multi-Language Campaigns",
      description: "Create campaigns in 50+ languages with AI-powered translation and localization that maintains your brand voice."
    },
    {
      icon: Target,
      title: "Target Any Country",
      description: "Launch in new markets instantly with location-specific targeting, local search trends, and cultural adaptation."
    },
    {
      icon: Users,
      title: "No Localization Team Needed",
      description: "Skip the expensive localization consultants. Our AI handles cultural nuances, local keywords, and market-specific messaging."
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Upload Your Brand Info",
      description: "Provide your existing campaign materials in any language. Our AI analyzes your brand voice and messaging."
    },
    {
      step: "2", 
      title: "Select Target Markets",
      description: "Choose countries and languages. We automatically research local keywords, competition, and cultural preferences."
    },
    {
      step: "3",
      title: "AI Creates Localized Campaigns",
      description: "Watch as we generate native-quality ads in multiple languages, complete with local keywords and cultural adaptation."
    },
    {
      step: "4",
      title: "Launch & Scale Globally",
      description: "Deploy campaigns across markets simultaneously. Monitor performance and expand to new regions with one click."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-50 via-white to-purple-50">
          <div className="container mx-auto max-w-6xl text-center">
            <Badge className="px-4 py-2 bg-blue-600 text-white border-blue-700 mb-6">
              <Globe className="w-4 h-4 mr-2" />
              Global Expansion Made Simple
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Your Google Ads,<br />In Any Language
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-4xl mx-auto">
              Scale your business globally with AI-powered campaigns that speak every customer's language. 
              Go from local to global in minutes, not months.
            </p>
            
            <div className="flex justify-center items-center mb-12">
              <Button 
                size="lg" 
                className="px-8 py-4 text-lg bg-blue-600 hover:bg-blue-700"
                onClick={() => window.location.href = getAuthUrl()}
              >
                Go Global Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            <div className="flex flex-wrap justify-center gap-8 text-sm text-muted-foreground">
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                50+ Languages Supported
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                195+ Countries Available
              </div>
              <div className="flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                Cultural Adaptation Included
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Launch Globally in Minutes, Not Months
              </h2>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
                Our AI understands cultural nuances, local search behavior, and market-specific messaging 
                to create campaigns that feel native to each region.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="border-blue-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                        <IconComponent className="h-6 w-6 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Video Demo Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto max-w-6xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              See How International Companies Scale Globally
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Watch how international companies create localized campaigns in multiple languages and markets. 
              From local to global in minutes, not months.
            </p>
            
            <div className="relative max-w-4xl mx-auto mb-12">
              <div className="aspect-video rounded-2xl overflow-hidden border-2 border-primary/20">
                <iframe
                  src="https://www.youtube-nocookie.com/embed/G5pY2KFHcC4?rel=0"
                  title="Upfixe International Campaigns Demo: German Personio.de Walkthrough"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>

            <p className="text-left max-w-3xl mx-auto mb-8 text-muted-foreground leading-relaxed">
              In the walkthrough above, we set up a campaign for a German company (personio.de) entirely in German — picking the brand, entering
              the landing page URL, a €300 daily budget and €100 target CPA, then switching geography to Germany and language to German from the
              same form used for English campaigns. Clicking "Generate" has Upfixe scan the landing page in German, propose a campaign goal and
              positive keywords in German, and — a couple of minutes later — return 144 keywords sorted by local search volume, competition and
              bid estimate, arranged into five ad groups with German headlines and descriptions. You don't need to speak the target language to
              review it: export the ad groups as CSV or JSON and share them with a native-speaking colleague or the client before publishing.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                size="lg" 
                className="px-8 py-4 text-lg bg-blue-600 hover:bg-blue-700"
                onClick={() => window.location.href = getAuthUrl()}
              >
                Go Global Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8"
                onClick={() => window.open('https://youtu.be/cICM0YxpOA0', '_blank')}
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Full Demo
              </Button>
            </div>
            
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                Multi-Country Targeting
              </div>
              <div className="flex items-center">
                <MessageCircle className="h-4 w-4 mr-2" />
                50+ Languages Supported
              </div>
              <div className="flex items-center">
                <TrendingUp className="h-4 w-4 mr-2" />
                Localized Keywords
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                How It Works
              </h2>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
                From brand upload to global launch in 4 simple steps
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-bold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.description}</p>
                  {index < howItWorks.length - 1 && (
                    <ArrowRight className="h-6 w-6 text-blue-600 mx-auto mt-4 hidden lg:block" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Go Global?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Start your global growth journey today with AI-powered campaigns.
            </p>
            
            <div className="flex justify-center items-center">
              <Button 
                size="lg" 
                variant="secondary"
                className="px-8 py-4 text-lg bg-white text-blue-600 hover:bg-gray-100"
                onClick={() => window.location.href = getAuthUrl()}
              >
                Go Global Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            <p className="text-sm mt-6 opacity-75">
              No setup fees • Free plan forever • Cancel anytime
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default InternationalPage;